import type { TranscriptSnippet } from "@expert/shared-types";

import { Card, Flex, Stack, Text } from "@mantine/core";
import { type JSX, useState } from "react";

import { isVoiceTask, useActiveTask } from "@/sdk";
import { AssistTextHighlight } from "@/timeline/AssistTextHighlight";
import { useTranscriptSnippetListener } from "@/transcript/hooks/useTranscriptSnippetListener";

import styles from "./RedesignedTranscript.module.css";
import { RedesignedTranscriptBody } from "./RedesignedTranscriptBody";

/**
 * Live transcript for the redesigned voice workspace (always-expanded, fills its parent panel).
 * Kept separate from the legacy rail transcript (`@/transcript/Transcript`) so redesign styling
 * can never leak into the legacy/mcafee path; the non-styling GAIA transcript hook is reused.
 */
export function RedesignedTranscript({ taskStartTimeInMs }: { taskStartTimeInMs: number }): JSX.Element | undefined {
    const [snippets, setSnippets] = useState<TranscriptSnippet[] | undefined>(undefined);

    const activeTask = useActiveTask();
    const activeVoiceTask = isVoiceTask(activeTask) ? activeTask : null;
    const isCallLive = activeVoiceTask !== null && activeVoiceTask.status === "assigned";
    const callScope = { activeCallSid: isCallLive ? activeVoiceTask.agentCallId : undefined };

    useTranscriptSnippetListener(setSnippets, callScope);

    return (
        <Card w="100%" h="100%" p={0} radius={0} bg="dark.8" flex="1 1 0">
            <Flex w="100%" h="100%" direction="column">
                <Stack w="100%" h="100%" gap={0} className={styles.stack}>
                    <Flex align="center" justify="space-between" w="100%" p="md" wrap="wrap" className={styles.header}>
                        <Text size="sm" fw="var(--mantine-font-weight-heavy)" c="dark.1">
                            Live transcript
                        </Text>
                    </Flex>
                    <AssistTextHighlight surface="transcript">
                        <RedesignedTranscriptBody
                            snippets={snippets}
                            taskStartTimeInMs={taskStartTimeInMs}
                            showTranscribing={isCallLive}
                        />
                    </AssistTextHighlight>
                </Stack>
            </Flex>
        </Card>
    );
}
